import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { CurrencyIcon, CurrencyIconSlot } from "../../../../Components/Atoms/CurrencyIcon";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { useContaInternacionalMovements } from "../../../../Hooks/useContaInternacionalHistory";
import {
  formatCurrencyAmount,
  getCiStatusLabel,
  getMovementKindMeta,
} from "../constants";
import {
  EMPTY_MOVEMENT_FILTERS,
  applyMovementFilters,
} from "../contaInternacionalMovementFilters";
import { ContaInternacionalMovementDetailModal } from "./ContaInternacionalMovementDetailModal";
import { MovementFilterBar } from "./MovementFilterBar";

const DEFAULT_ROWS_PER_PAGE = 10;

const StatusBadge = ({ status }) => {
  const label = getCiStatusLabel(status);
  const isPending = String(status || "").toLowerCase().includes("process");
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        isPending
          ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
      }`}
    >
      {label}
    </span>
  );
};

const TypeBadge = ({ kind }) => {
  const meta = getMovementKindMeta()[kind];
  if (!meta) return null;

  return (
    <span
      className={`inline-flex w-[7rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badgeClass}`}
    >
      {meta.label}
    </span>
  );
};

const HistoryHeaderRow = () => (
  <GridHistoryHeader colSpan={4}>
    <span>{intl.get("grid.movement")}</span>
    <span>{intl.get("grid.date")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>{intl.get("grid.value")}</span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("grid.status")} />
  </GridHistoryHeader>
);

export const MovementsList = ({ currencyId, title }) => {
  const resolvedTitle = title ?? intl.get("contaInternacional.recentMovements");
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState(EMPTY_MOVEMENT_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const {
    items: allMovements,
    loading: movementsLoading,
    error: movementsError,
    reload,
  } = useContaInternacionalMovements({ currencyId });

  const filteredMovements = useMemo(
    () => applyMovementFilters(allMovements, filters),
    [allMovements, filters],
  );

  const totalItems = filteredMovements.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedMovements = useMemo(() => {
    if (totalItems === 0) return [];
    const start = (page - 1) * rowsPerPage;
    return filteredMovements.slice(start, start + rowsPerPage);
  }, [filteredMovements, page, rowsPerPage, totalItems]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">{resolvedTitle}</h2>
      <div className="mb-4">
        <MovementFilterBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filteredItems={filteredMovements}
          currencyId={currencyId}
        />
      </div>

      <div className="app-card overflow-hidden">
        {movementsLoading ? (
          <div className="app-empty-state m-4">
            {intl.get("common.loading") || "Carregando…"}
          </div>
        ) : movementsError ? (
          <div className="m-4 space-y-3">
            <p className="text-sm text-gray-500">
              {intl.get("contaInternacional.balancesLoadError")}
            </p>
            <button type="button" className="btn-outline" onClick={reload}>
              {intl.get("contaInternacional.retryLoad")}
            </button>
          </div>
        ) : paginatedMovements.length === 0 ? (
          <div className="app-empty-state m-4">
            {intl.get("contaInternacional.noMovementsFiltered")}
          </div>
        ) : (
          <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4}>
            <HistoryHeaderRow />
            {paginatedMovements.map((item) => (
              <GridHistoryRow key={item.id} colSpan={4}>
                <GridHistoryCell label={intl.get("grid.movement")}>
                  <div className="flex min-w-0 items-center gap-3">
                    <CurrencyIconSlot>
                      <CurrencyIcon asset={item.currency} />
                    </CurrencyIconSlot>
                    <TypeBadge kind={item.kind} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </GridHistoryCell>
                <GridHistoryCell label={intl.get("grid.date")}>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
                </GridHistoryCell>
                <GridHistoryCell label={intl.get("grid.value")} align="right">
                  <p
                    className={`text-sm font-semibold ${
                      item.amount >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-brand-dark dark:text-gray-100"
                    }`}
                  >
                    {item.amount >= 0 ? "+" : ""}
                    {formatCurrencyAmount(Math.abs(item.amount), item.currency)}
                  </p>
                </GridHistoryCell>
                <GridHistoryStatusActionsCell
                  statusLabel={intl.get("grid.status")}
                  statusBadge={<StatusBadge status={item.status} />}
                  onShowDetail={setSelectedItem}
                  item={item}
                />
              </GridHistoryRow>
            ))}
          </GridHistoryContainer>
        )}

        <GridPagination
          total={totalItems}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel={intl.get("contaInternacional.movementsItemLabel")}
        />
      </div>

      <ContaInternacionalMovementDetailModal
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </section>
  );
};
